import { maunaSpaceAPI } from '@/api';
import MaunaHeader from './MaunaHeader';
import MaunaMissionRewardStatistics from './MaunaMissionRewardStatistics';
import MaunaPageAnalytics from './MaunaPageAnalytics';
import {
  Content,
  LoadingMessage,
  SectionContainer,
  SectionContent,
  SectionHeader,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper,
} from '@/components/CommonStyles';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ReactApexChart from 'react-apexcharts';
import { palette } from '@/modules/defines/styles';
import '@/modules/defines/font.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type StatisticsTabType = 'users' | 'missionReward' | 'pages';

const PAGE_ANALYTICS_REVAMP_DATE = '2026-07-16';

const getKstToday = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const MaunaDataContainer = () => {
  const [statisticsTab, setStatisticsTab] =
    useState<StatisticsTabType>('users');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('monthly');
  const [screenAccessType, setScreenAccessType] = useState<'total' | 'unique'>(
    'total'
  );
  const [expandedLegends, setExpandedLegends] = useState<{
    [key: number]: boolean;
  }>({});
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [hiddenSeries, setHiddenSeries] = useState<{ [key: number]: string[] }>(
    {}
  );
  const chartScrollRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // LOAD Data
  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: ['getStatisticsMauna'],
    queryFn: () =>
      maunaSpaceAPI.getStatistics({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  useEffect(() => {
    if (appliedStartDate || appliedEndDate) return;

    const dates = dataAPI?.data?.dates as string[] | undefined;
    if (!dates?.length) return;

    const startDate = dates[0].slice(0, 10);
    const endDate = getKstToday();
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  }, [appliedEndDate, appliedStartDate, dataAPI]);

  const isDateRangeValid = Boolean(
    draftStartDate &&
      draftEndDate &&
      draftStartDate <= draftEndDate &&
      (statisticsTab !== 'pages' ||
        (draftStartDate >= PAGE_ANALYTICS_REVAMP_DATE &&
          draftEndDate >= PAGE_ANALYTICS_REVAMP_DATE))
  );

  const applyDateRange = () => {
    if (!isDateRangeValid) return;
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
  };

  const selectStatisticsTab = (tab: StatisticsTabType) => {
    setStatisticsTab(tab);

    if (tab !== 'pages') return;

    const today = getKstToday();
    const nextStartDate =
      !draftStartDate || draftStartDate < PAGE_ANALYTICS_REVAMP_DATE
        ? PAGE_ANALYTICS_REVAMP_DATE
        : draftStartDate;
    const nextEndDate =
      !draftEndDate || draftEndDate < PAGE_ANALYTICS_REVAMP_DATE
        ? today
        : draftEndDate;
    const nextAppliedStartDate =
      !appliedStartDate || appliedStartDate < PAGE_ANALYTICS_REVAMP_DATE
        ? PAGE_ANALYTICS_REVAMP_DATE
        : appliedStartDate;
    const nextAppliedEndDate =
      !appliedEndDate || appliedEndDate < PAGE_ANALYTICS_REVAMP_DATE
        ? today
        : appliedEndDate;

    setDraftStartDate(nextStartDate);
    setDraftEndDate(nextEndDate);
    setAppliedStartDate(nextAppliedStartDate);
    setAppliedEndDate(nextAppliedEndDate);
  };

  const toggleLegend = (chartIndex: number) => {
    setExpandedLegends((prev) => ({
      ...prev,
      [chartIndex]: !prev[chartIndex],
    }));
  };

  const handleLegendHover = (seriesName: string, chartIndex: number) => {
    setHoveredSeries(seriesName);

    const chartContainers = document.querySelectorAll(
      `.chart-container-${chartIndex}`
    );
    chartContainers.forEach((container) => {
      container.classList.add('series-highlighted');

      const seriesElements = container.querySelectorAll('.apexcharts-series');
      seriesElements.forEach((series, index) => {
        const currentChart = chartData?.[chartIndex];
        if (currentChart && currentChart.series[index]?.name === seriesName) {
          series.classList.add('highlighted');
        } else {
          series.classList.remove('highlighted');
        }
      });
    });
  };

  const handleLegendLeave = (chartIndex: number) => {
    setHoveredSeries(null);

    const chartContainers = document.querySelectorAll(
      `.chart-container-${chartIndex}`
    );
    chartContainers.forEach((container) => {
      container.classList.remove('series-highlighted');

      const seriesElements = container.querySelectorAll('.apexcharts-series');
      seriesElements.forEach((series) => {
        series.classList.remove('highlighted');
      });
    });
  };

  // Bar Chart 옵션
  const barChartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: 'x' as const,
        autoScaleYaxis: true,
      },
      selection: {
        enabled: true,
        type: 'x' as const,
      },
      pan: {
        enabled: true,
        type: 'x' as const,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded',
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val === 0 ? '' : val;
      },
      style: {
        fontSize: '11px',
        fontWeight: '600',
        colors: ['#111827'],
      },
    },
    stroke: {
      show: false,
      width: 1,
      colors: ['transparent'],
    },
    xaxis: {
      title: {
        text: '날짜',
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: '이용자 수',
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      y: {
        formatter: function (val: number) {
          return val + ' 명';
        },
      },
    },
  };

  // Line Chart 옵션
  const lineChartOptions = {
    chart: {
      type: 'line' as const,
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      title: {
        text: '날짜',
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: '접속자 수',
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];

        if (value === 0) {
          return '';
        }

        let tooltipContent = `<div class="apexcharts-tooltip-box" style="background: rgba(0, 0, 0, 0.8); color: white; padding: 8px; border-radius: 4px; font-size: 10px; line-height: 1.2;">`;

        const validSeries = [];
        for (let i = 0; i < series.length; i++) {
          const seriesValue = series[i][dataPointIndex];
          if (seriesValue > 0) {
            validSeries.push({
              name: w.globals.seriesNames[i],
              value: seriesValue,
            });
          }
        }

        validSeries.sort((a, b) => b.value - a.value);

        const maxItems = 16;
        const itemsToShow = validSeries.slice(0, maxItems);
        const hasMoreItems = validSeries.length > maxItems;

        itemsToShow.forEach((item) => {
          tooltipContent += `<span>${item.name}: <strong style="font-weight: 800">${item.value} 회</strong></span><br/>`;
        });

        if (hasMoreItems) {
          tooltipContent += `<span style="color: #888; font-style: italic;">... 외 ${
            validSeries.length - maxItems
          }개 항목</span><br/>`;
        }

        tooltipContent += `</div>`;
        return tooltipContent;
      },
    },
  };

  // 월별 데이터 집계 함수
  const aggregateMonthlyData = (dates: string[], data: number[] = []) => {
    const monthlyMap: { [key: string]: number } = {};

    dates.forEach((date, index) => {
      const month = date.substring(0, 7); // "2025-10" 형식
      if (!monthlyMap[month]) {
        monthlyMap[month] = 0;
      }
      monthlyMap[month] += data[index] ?? 0;
    });

    const sortedMonths = Object.keys(monthlyMap).sort();
    const monthlyData = sortedMonths.map((month) => monthlyMap[month]);

    return {
      categories: sortedMonths,
      data: monthlyData,
    };
  };

  // CHART DATA
  const chartData = useMemo(() => {
    if (!dataAPI?.data) return null;

    const { dates = [], data1, data2, data3, data4 } = dataAPI.data;
    const sourceDates = dates as string[];
    const filteredIndexes: number[] = sourceDates.reduce(
      (indexes: number[], date: string, index: number) => {
        const dateValue = date.slice(0, 10);
        const isAfterStart =
          !appliedStartDate || dateValue >= appliedStartDate;
        const isBeforeEnd = !appliedEndDate || dateValue <= appliedEndDate;

        if (isAfterStart && isBeforeEnd) indexes.push(index);
        return indexes;
      },
      []
    );
    const filteredDates = filteredIndexes.map((index: number) =>
      sourceDates[index]
    );
    const normalizeDailyData = (data: number[] = []) =>
      filteredIndexes.map((index: number) => data[index] ?? 0);

    // 첫 번째 차트 데이터 (일별/월별 전환)
    let chart1Categories = filteredDates;
    let chart1TotalData = normalizeDailyData(data1);
    let chart1UniqueData = normalizeDailyData(data3);

    if (viewType === 'monthly') {
      const monthlyResult = aggregateMonthlyData(
        filteredDates,
        chart1TotalData
      );
      const monthlyUniqueResult = aggregateMonthlyData(
        filteredDates,
        chart1UniqueData
      );
      chart1Categories = monthlyResult.categories;
      chart1TotalData = monthlyResult.data;
      chart1UniqueData = monthlyUniqueResult.data;
    }

    const screenAccessData = screenAccessType === 'total' ? data2 : data4;

    return [
      {
        id: 1,
        title: '서비스 이용 수',
        description: ' ',
        categories: chart1Categories,
        series: [
          {
            name: 'Total',
            data: chart1TotalData,
          },
          {
            name: 'Unique',
            data: chart1UniqueData,
          },
        ],
        options: barChartOptions,
        type: 'bar',
      },
      {
        id: 2,
        title: '화면별 접속 수',
        description: ' ',
        categories: filteredDates,
        series: (screenAccessData || []).map((item: any) => ({
          name: item.label,
          data: filteredIndexes.map(
            (index: number) => item.value[index] ?? 0
          ),
        })),
        options: lineChartOptions,
        type: 'line',
      },
    ];
  }, [
    appliedEndDate,
    appliedStartDate,
    dataAPI,
    screenAccessType,
    viewType,
  ]);

  useEffect(() => {
    if (!chartData) return;

    const scrollTimer = window.setTimeout(() => {
      Object.values(chartScrollRefs.current).forEach((container) => {
        if (!container) return;
        container.scrollLeft = container.scrollWidth - container.clientWidth;
      });
    }, 100);

    return () => window.clearTimeout(scrollTimer);
  }, [chartData]);

  const scrollChart = (chartIndex: number, direction: -1 | 1) => {
    const container = chartScrollRefs.current[chartIndex];
    if (!container) return;

    container.scrollBy({
      left: direction * Math.max(container.clientWidth * 0.8, 320),
      behavior: 'smooth',
    });
  };

  // 필터링된 시리즈 데이터 생성 (색상 보존)
  const getFilteredSeries = (chartIndex: number) => {
    if (!chartData) return [];

    const chart = chartData[chartIndex];
    if (!chart) return [];

    const defaultColors = [
      '#008FFB',
      '#00E396',
      '#FEB019',
      '#FF4560',
      '#775DD0',
      '#546E7A',
      '#26a69a',
      '#D10CE8',
      '#FF9800',
      '#795548',
    ];

    const hiddenSeriesForChart = hiddenSeries[chartIndex] || [];
    return chart.series
      .filter((series: any) => !hiddenSeriesForChart.includes(series.name))
      .map((series: any) => {
        const originalIndex = chart.series.findIndex(
          (s: any) => s.name === series.name
        );
        return {
          ...series,
          color: defaultColors[originalIndex % defaultColors.length],
        };
      });
  };

  // 차트용 데이터 슬라이싱 함수
  const getChartData = (chart: any) => {
    if (!chart) {
      return {
        categories: chart?.categories || [],
        series: chart?.series || [],
        needsScroll: false,
      };
    }

    const totalLength = chart.categories.length;

    if (totalLength <= 14) {
      return {
        categories: chart.categories,
        series: chart.series,
        needsScroll: false,
      };
    }

    return {
      categories: chart.categories,
      series: chart.series,
      needsScroll: true,
    };
  };

  return (
    <Container>
      <MaunaHeader />
      <Content>
        <StatisticsToolbar>
          <StatisticsTabs role="tablist" aria-label="통계 구분">
            <StatisticsTab
              type="button"
              role="tab"
              aria-selected={statisticsTab === 'users'}
              $active={statisticsTab === 'users'}
              onClick={() => selectStatisticsTab('users')}
            >
              이용자
            </StatisticsTab>
            <StatisticsTab
              type="button"
              role="tab"
              aria-selected={statisticsTab === 'missionReward'}
              $active={statisticsTab === 'missionReward'}
              onClick={() => selectStatisticsTab('missionReward')}
            >
              미션&amp;리워드
            </StatisticsTab>
            <StatisticsTab
              type="button"
              role="tab"
              aria-selected={statisticsTab === 'pages'}
              $active={statisticsTab === 'pages'}
              onClick={() => selectStatisticsTab('pages')}
            >
              페이지
            </StatisticsTab>
          </StatisticsTabs>
          <DateFilter>
            <DateFilterLabel>기간</DateFilterLabel>
            <DateInput
              type="date"
              aria-label="통계 시작일"
              value={draftStartDate}
              min={
                statisticsTab === 'pages'
                  ? PAGE_ANALYTICS_REVAMP_DATE
                  : undefined
              }
              max={draftEndDate || getKstToday()}
              onInput={(event) =>
                setDraftStartDate((event.target as HTMLInputElement).value)
              }
            />
            <DateSeparator>~</DateSeparator>
            <DateInput
              type="date"
              aria-label="통계 종료일"
              value={draftEndDate}
              min={
                statisticsTab === 'pages'
                  ? draftStartDate > PAGE_ANALYTICS_REVAMP_DATE
                    ? draftStartDate
                    : PAGE_ANALYTICS_REVAMP_DATE
                  : draftStartDate
              }
              max={getKstToday()}
              onInput={(event) =>
                setDraftEndDate((event.target as HTMLInputElement).value)
              }
            />
            <DateApplyButton
              type="button"
              disabled={!isDateRangeValid}
              onClick={applyDateRange}
            >
              조회
            </DateApplyButton>
          </DateFilter>
        </StatisticsToolbar>
        <SectionWrapper>
          {statisticsTab === 'missionReward' ? (
            appliedStartDate && appliedEndDate ? (
              <MaunaMissionRewardStatistics
                startDate={appliedStartDate}
                endDate={appliedEndDate}
              />
            ) : (
              <SectionContainer>
                <LoadingMessage>통계 기간을 설정하고 있습니다...</LoadingMessage>
              </SectionContainer>
            )
          ) : statisticsTab === 'pages' ? (
            appliedStartDate && appliedEndDate ? (
              <MaunaPageAnalytics
                startDate={appliedStartDate}
                endDate={appliedEndDate}
              />
            ) : (
              <SectionContainer>
                <LoadingMessage>통계 기간을 설정하고 있습니다...</LoadingMessage>
              </SectionContainer>
            )
          ) : (
            <>
              {isLoadingAPI ? (
                <SectionContainer>
                  <LoadingMessage>
                    서비스 이용 통계를 불러오는 중입니다...
                  </LoadingMessage>
                </SectionContainer>
              ) : !chartData ||
                chartData.length === 0 ||
                chartData.every((chart) => chart.categories.length === 0) ? (
                <SectionContainer>
                  <LoadingMessage>
                    선택 기간의 서비스 이용 통계가 없습니다.
                  </LoadingMessage>
                </SectionContainer>
              ) : (
                chartData.map((chart, index) => (
              <SectionContainer key={chart.id}>
                {index === 0 ? (
                  <CustomSectionHeader>
                    <TitleContainer>
                      <SectionTitle>{chart.title}</SectionTitle>
                      <SectionSubTitle>{chart.description}</SectionSubTitle>
                    </TitleContainer>
                    <TabContainer>
                      <Tab
                        $active={viewType === 'monthly'}
                        onClick={() => setViewType('monthly')}
                      >
                        월별
                      </Tab>
                      <Tab
                        $active={viewType === 'daily'}
                        onClick={() => setViewType('daily')}
                      >
                        일별
                      </Tab>
                    </TabContainer>
                  </CustomSectionHeader>
                ) : index === 1 ? (
                  <CustomSectionHeader>
                    <TitleContainer>
                      <SectionTitle>{chart.title}</SectionTitle>
                      <SectionSubTitle>{chart.description}</SectionSubTitle>
                    </TitleContainer>
                    <TabContainer>
                      <Tab
                        $active={screenAccessType === 'total'}
                        onClick={() => setScreenAccessType('total')}
                      >
                        Total
                      </Tab>
                      <Tab
                        $active={screenAccessType === 'unique'}
                        onClick={() => setScreenAccessType('unique')}
                      >
                        Unique
                      </Tab>
                    </TabContainer>
                  </CustomSectionHeader>
                ) : (
                  <SectionHeader>
                    <SectionTitle>{chart.title}</SectionTitle>
                    <SectionSubTitle>{chart.description}</SectionSubTitle>
                  </SectionHeader>
                )}
                <SectionContent>
                  {(() => {
                    const chartDataInfo = getChartData(chart);

                    if (chartDataInfo.needsScroll) {
                      return (
                        <ChartWithFixedTitles>
                          <ChartScrollControls>
                            <ChartScrollButton
                              type="button"
                              aria-label={`${chart.title} 이전 구간 보기`}
                              title="이전 구간 보기"
                              onClick={() => scrollChart(index, -1)}
                            >
                              <ChevronLeft size={18} aria-hidden="true" />
                            </ChartScrollButton>
                            <ChartScrollButton
                              type="button"
                              aria-label={`${chart.title} 다음 구간 보기`}
                              title="다음 구간 보기"
                              onClick={() => scrollChart(index, 1)}
                            >
                              <ChevronRight size={18} aria-hidden="true" />
                            </ChartScrollButton>
                          </ChartScrollControls>
                          <FixedAxisTitles>
                            {chart.options.xaxis?.title?.text && (
                              <XAxisTitle>
                                {chart.options.xaxis.title.text}
                              </XAxisTitle>
                            )}
                            {chart.options.yaxis?.title?.text && (
                              <YAxisTitle>
                                {chart.options.yaxis.title.text}
                              </YAxisTitle>
                            )}
                          </FixedAxisTitles>
                          <ScrollableChartWrapper
                            className={`chart-container-${index}`}
                            ref={(node) => {
                              chartScrollRefs.current[index] = node;
                            }}
                            tabIndex={0}
                            aria-label={`${chart.title} 좌우 스크롤 영역`}
                            onKeyDown={(event) => {
                              if (event.key === 'ArrowLeft') {
                                event.preventDefault();
                                scrollChart(index, -1);
                              }
                              if (event.key === 'ArrowRight') {
                                event.preventDefault();
                                scrollChart(index, 1);
                              }
                            }}
                          >
                            <ScrollableChart $isBarChart={chart.type === 'bar'}>
                              <ReactApexChart
                                options={
                                  {
                                    ...chart.options,
                                    chart: {
                                      ...chart.options.chart,
                                      width:
                                        chart.type === 'bar' ? '300%' : '200%',
                                      toolbar: {
                                        show: false,
                                      },
                                    } as any,
                                    xaxis: {
                                      ...chart.options.xaxis,
                                      categories: chartDataInfo.categories,
                                      title: {
                                        ...chart.options.xaxis?.title,
                                        text: undefined,
                                      },
                                    },
                                    yaxis: {
                                      ...chart.options.yaxis,
                                      title: {
                                        ...chart.options.yaxis?.title,
                                        text: undefined,
                                      },
                                      labels: {
                                        show: true,
                                        style: {
                                          fontSize: '11px',
                                          fontWeight: '400',
                                          colors: ['#6B7280'],
                                        },
                                      },
                                    },
                                    stroke: {
                                      curve: 'smooth',
                                      width: 2,
                                    },
                                    states: {
                                      hover: {
                                        filter: {
                                          type: 'none',
                                        },
                                      },
                                      active: {
                                        allowMultipleDataPointsSelection: false,
                                        filter: {
                                          type: 'none',
                                        },
                                      },
                                    },
                                    legend: {
                                      show: false,
                                    },
                                    plotOptions:
                                      chart.type === 'bar'
                                        ? {
                                            ...(chart.options as any)
                                              ?.plotOptions,
                                            bar: {
                                              ...(chart.options as any)
                                                ?.plotOptions?.bar,
                                              columnWidth: '40%',
                                            },
                                          }
                                        : (chart.options as any)?.plotOptions ||
                                          {},
                                  } as any
                                }
                                series={getFilteredSeries(index)}
                                type={chart.type as any}
                                height={350}
                              />
                            </ScrollableChart>
                          </ScrollableChartWrapper>
                          {chart.type === 'line' && (
                            <FixedLegendContainer>
                              <LineChartLegendContainer>
                                {chart.series.map(
                                  (series: any, seriesIndex: number) => {
                                    const hiddenSeriesForChart =
                                      hiddenSeries[index] || [];
                                    const isHidden =
                                      hiddenSeriesForChart.includes(
                                        series.name
                                      );
                                    const isHovered =
                                      hoveredSeries === series.name;

                                    const defaultColors = [
                                      '#008FFB',
                                      '#00E396',
                                      '#FEB019',
                                      '#FF4560',
                                      '#775DD0',
                                      '#546E7A',
                                      '#26a69a',
                                      '#D10CE8',
                                      '#FF9800',
                                      '#795548',
                                    ];

                                    const originalIndex = chart.series.findIndex(
                                      (s: any) => s.name === series.name
                                    );
                                    const seriesColor =
                                      defaultColors[
                                        originalIndex % defaultColors.length
                                      ];

                                    return (
                                      <LineChartLegendItem
                                        key={seriesIndex}
                                        $isHovered={isHovered}
                                        $isHidden={isHidden}
                                        $seriesColor={seriesColor}
                                        onMouseEnter={() =>
                                          handleLegendHover(series.name, index)
                                        }
                                        onMouseLeave={() =>
                                          handleLegendLeave(index)
                                        }
                                        onClick={() => {
                                          const currentHidden =
                                            hiddenSeries[index] || [];
                                          const isCurrentlyHidden =
                                            currentHidden.includes(series.name);

                                          if (isCurrentlyHidden) {
                                            setHiddenSeries((prev) => ({
                                              ...prev,
                                              [index]: currentHidden.filter(
                                                (name) => name !== series.name
                                              ),
                                            }));
                                          } else {
                                            setHiddenSeries((prev) => ({
                                              ...prev,
                                              [index]: [
                                                ...currentHidden,
                                                series.name,
                                              ],
                                            }));
                                          }
                                        }}
                                      >
                                        <LineChartLegendColor
                                          $color={seriesColor}
                                        />
                                        <LineChartLegendName>
                                          {series.name}
                                        </LineChartLegendName>
                                      </LineChartLegendItem>
                                    );
                                  }
                                )}
                              </LineChartLegendContainer>
                            </FixedLegendContainer>
                          )}
                        </ChartWithFixedTitles>
                      );
                    }

                    return (
                      <ChartContainer className={`chart-container-${index}`}>
                        <ReactApexChart
                          options={
                            {
                              ...chart.options,
                              stroke: {
                                curve: 'smooth',
                                width: 2,
                              },
                              states: {
                                hover: {
                                  filter: {
                                    type: 'none',
                                  },
                                },
                                active: {
                                  allowMultipleDataPointsSelection: false,
                                  filter: {
                                    type: 'none',
                                  },
                                },
                              },
                              chart: {
                                ...chart.options.chart,
                                toolbar: {
                                  ...chart.options.chart?.toolbar,
                                  show: chart.type === 'bar',
                                },
                              } as any,
                              xaxis: {
                                ...chart.options.xaxis,
                                categories: chartDataInfo.categories,
                              },
                              yaxis: {
                                ...chart.options.yaxis,
                                labels: {
                                  show: true,
                                  style: {
                                    fontSize: '11px',
                                    fontWeight: '400',
                                    colors: ['#6B7280'],
                                  },
                                },
                              },
                            } as any
                          }
                          series={chartDataInfo.series}
                          type={chart.type as any}
                          height={350}
                        />
                      </ChartContainer>
                    );
                  })()}
                </SectionContent>
              </SectionContainer>
                ))
              )}
            </>
          )}
        </SectionWrapper>
      </Content>
    </Container>
  );
};

export default MaunaDataContainer;

// Styled Components
const StatisticsToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);

  @media (max-width: 860px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StatisticsTabs = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 8px;
  background: #eef2f7;
`;

const StatisticsTab = styled.button<{ $active: boolean }>`
  min-width: 110px;
  padding: 0.55rem 0.9rem;
  border: 0;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? '#1f6feb' : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#475569')};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? '#1859bd' : '#e2e8f0')};
  }
`;

const DateFilter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto;
  }
`;

const DateFilterLabel = styled.span`
  margin-right: 0.15rem;
  color: #334155;
  font-size: 0.82rem;
  font-weight: 700;

  @media (max-width: 600px) {
    grid-column: 1 / -1;
  }
`;

const DateInput = styled.input`
  width: 142px;
  height: 36px;
  padding: 0 0.55rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #0f172a;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  font-size: 0.8rem;

  &:focus {
    outline: none;
    border-color: #1f6feb;
    box-shadow: 0 0 0 2px rgba(31, 111, 235, 0.14);
  }

  @media (max-width: 600px) {
    width: 100%;
    min-width: 0;
  }
`;

const DateSeparator = styled.span`
  color: #94a3b8;
  font-size: 0.8rem;
`;

const DateApplyButton = styled.button`
  height: 36px;
  padding: 0 0.9rem;
  border: 0;
  border-radius: 6px;
  background: #334155;
  color: #ffffff;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1e293b;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChartContainer = styled.div`
  width: 100%;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .apexcharts-tooltip {
    z-index: 9999 !important;
  }

  .apexcharts-series path,
  .apexcharts-series circle {
    transition: opacity 0.3s ease;
  }

  &.series-highlighted .apexcharts-series path,
  &.series-highlighted .apexcharts-series circle {
    opacity: 0.3;
  }

  &.series-highlighted .apexcharts-series.highlighted path,
  &.series-highlighted .apexcharts-series.highlighted circle {
    opacity: 1;
    stroke-width: 3;
  }

  .apexcharts-toolbar {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 8px;
  }

  .apexcharts-toolbar-icon {
    fill: #6b7280;
    cursor: pointer;
    transition: fill 0.2s ease;

    &:hover {
      fill: #374151;
    }
  }

  .apexcharts-pan-icon,
  .apexcharts-download-icon,
  .apexcharts-zoom-icon,
  .apexcharts-zoomin-icon,
  .apexcharts-zoomout-icon,
  .apexcharts-reset-icon {
    fill: #6b7280;
    cursor: pointer;
    transition: fill 0.2s ease;

    &:hover {
      fill: #374151;
    }
  }

  .apexcharts-canvas {
    overflow: visible;
  }

  .apexcharts-zoomable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .apexcharts-selection-rect {
    fill: rgba(59, 130, 246, 0.1);
    stroke: #3b82f6;
    stroke-width: 1;
    stroke-dasharray: 4 4;
  }
`;

const ScrollableChartWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 8px;
  padding-bottom: 4px;
  scroll-behavior: smooth;
  scrollbar-gutter: stable;
  overscroll-behavior-x: contain;
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  .apexcharts-tooltip {
    z-index: 9999 !important;
  }

  .apexcharts-series path,
  .apexcharts-series circle {
    transition: opacity 0.3s ease;
  }

  &.series-highlighted .apexcharts-series path,
  &.series-highlighted .apexcharts-series circle {
    opacity: 0.3;
  }

  &.series-highlighted .apexcharts-series.highlighted path,
  &.series-highlighted .apexcharts-series.highlighted circle {
    opacity: 1;
    stroke-width: 3;
  }

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    border-radius: 6px;
    border: 2px solid #f1f5f9;

    &:hover {
      background: linear-gradient(90deg, #94a3b8, #64748b);
    }
  }

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const ScrollableChart = styled.div<{ $isBarChart?: boolean }>`
  width: ${({ $isBarChart }) => ($isBarChart ? '300%' : '200%')};
  min-width: ${({ $isBarChart }) => ($isBarChart ? '1200px' : '800px')};
`;

const ChartWithFixedTitles = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 60px 40px 60px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .apexcharts-tooltip {
    z-index: 9999 !important;
  }
`;

const ChartScrollControls = styled.div`
  position: absolute;
  top: 12px;
  right: 18px;
  z-index: 20;
  display: flex;
  overflow: hidden;
  border: 1px solid #dbe2ea;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
`;

const ChartScrollButton = styled.button`
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-right: 1px solid #e2e8f0;
  background: transparent;
  color: #334155;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:last-child {
    border-right: 0;
  }

  &:hover {
    background: #eaf2ff;
    color: #1d4ed8;
  }

  &:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid #2563eb;
    outline-offset: -2px;
  }
`;

const FixedAxisTitles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const XAxisTitle = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui,
    Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR',
    'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const YAxisTitle = styled.div`
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%) rotate(-90deg);
  transform-origin: center;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui,
    Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR',
    'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const FixedLegendContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 60px;
  right: 60px;
  z-index: 15;
  pointer-events: auto;
`;

const LineChartLegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
`;

const LineChartLegendItem = styled.div<{
  $isHovered?: boolean;
  $isHidden?: boolean;
  $seriesColor?: string;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 6px;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.4 : 1)};
  background: ${({ $isHovered, $seriesColor }) =>
    $isHovered ? `${$seriesColor}15` : 'transparent'};
  border: 1px solid
    ${({ $isHovered, $seriesColor }) =>
      $isHovered ? $seriesColor : 'transparent'};

  &:hover {
    background: ${({ $seriesColor }) => `${$seriesColor}15`};
    border-color: ${({ $seriesColor }) => $seriesColor};
    transform: translateY(-1px);
  }
`;

const LineChartLegendColor = styled.div<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LineChartLegendName = styled.span`
  white-space: nowrap;
`;

const CustomSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 8px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active }) =>
    $active ? palette.systemBlue : 'transparent'};
  color: ${({ $active }) => ($active ? 'white' : '#64748b')};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;

  &:hover {
    background: ${({ $active }) =>
      $active ? palette.systemBlue : 'rgba(100, 116, 139, 0.1)'};
  }
`;
